// Student assignment detail — one bundle. Items are shown in order; the
// "current" one (first non-completed) gets the primary "Start" CTA. After the
// student submits an item the existing runner navigates to /results; when they
// come back here, that item shows as complete and the next lights up. Simple,
// linear, and matches the "attempt in sequence" spec.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Loader2 } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';
import { prettyTask } from '@/lib/labels';

const EXAM_LABEL = { celpip: 'CELPIP', pte: 'PTE Core' };

export default function AssignmentDetailPage() {
  const { assignment_id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.myAssignment(assignment_id);
        setA(res.data.assignment);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Could not load assignment.');
      } finally { setLoading(false); }
    })();
  }, [assignment_id]);

  if (loading) return <AppShell><div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></AppShell>;
  if (error) return <AppShell><p className="text-sm text-rose-600">{error}</p></AppShell>;
  if (!a) return null;

  const currentIdx = a.items.findIndex((it) => !it.completed);
  const currentSetId = currentIdx >= 0 ? a.items[currentIdx].set_id : null;
  const pct = a.total_count ? Math.round((a.completed_count / a.total_count) * 100) : 0;
  const withAid = (p) => p + (p.includes('?') ? '&' : '?') + `assignment=${assignment_id}`;

  return (
    <AppShell>
      <Link to="/assignments" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> All assignments
      </Link>

      <div className="mt-3 border-b border-slate-200 pb-5">
        <p className="mono-stat">Assignment</p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-slate-900">{a.title}</h1>
        <p className="mt-1 text-sm text-slate-500">
          Assigned by {a.assigned_by_name}
          {a.due_at && ` · due ${new Date(a.due_at).toLocaleDateString()}`}
        </p>
        {a.description && <p className="mt-3 max-w-prose whitespace-pre-wrap text-sm italic text-slate-600" data-testid="detail-description">“{a.description}”</p>}
        <div className="mt-4 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="mono-stat">{a.completed_count} / {a.total_count} done</span>
        </div>
      </div>

      <ol className="mt-6 space-y-3" data-testid="detail-items">
        {a.items.map((it, i) => {
          const isCurrent = it.set_id === currentSetId;
          return (
            <li
              key={it.set_id}
              data-testid={`detail-item-${it.set_id}`}
              className={cn(
                'flex items-center gap-4 rounded border bg-white p-4',
                isCurrent ? 'border-indigo-500 ring-2 ring-indigo-100'
                          : it.completed ? 'border-emerald-100 bg-emerald-50/30' : 'border-slate-200'
              )}
            >
              <span className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                it.completed ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
              )}>
                {it.completed ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-[15px] font-semibold text-slate-900">{it.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">{prettyTask(it.task_type)} · {EXAM_LABEL[it.exam] || it.exam}</p>
              </div>
              {it.completed ? (
                <Link to={withAid(it.cta_path)} className="text-xs font-medium text-slate-500 hover:text-slate-900" data-testid={`detail-review-${it.set_id}`}>Review</Link>
              ) : (
                <Link
                  to={withAid(it.cta_path)}
                  data-testid={`detail-start-${it.set_id}`}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded px-3 py-1.5 text-xs font-semibold',
                    isCurrent ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  )}
                >
                  {isCurrent ? 'Start' : 'Skip to'} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
              {!it.completed && !isCurrent && (
                <Circle className="hidden h-4 w-4 text-slate-300" />
              )}
            </li>
          );
        })}
      </ol>

      {a.completed && (
        <div className="mt-8 rounded border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="detail-complete-banner">
          <p className="mono-stat text-emerald-700">Assignment complete</p>
          <p className="mt-1 text-sm text-emerald-800">You&apos;ve submitted every item. Nice work — your instructor can see your progress on their end.</p>
        </div>
      )}
    </AppShell>
  );
}
